import { useCallback, useEffect, useMemo } from 'react';
import type { ApprovalTypeReferrer } from '@/types/approval';
import {
  getValidFixedMembers,
  toFixedMemberIds,
} from '@/utils/approval/fixedApprovalMembers';

type UseFixedApprovalMembersResult = {
  /** 유효한 고정 항목 원본 */
  members: ApprovalTypeReferrer[];
  /** 이 id 가 어드민 고정 항목인지 — 삭제 버튼 노출/초기화 대상 판정 */
  isFixed: (id?: string | null) => boolean;
};

/**
 * 양식 고정 항목(fixedCoApprovers / fixedReferrers)을 화면에 '항상 존재하도록' 보정하고,
 * 고정 여부 판별기를 함께 돌려준다.
 *
 * 기존 1회 적용(useApplyFixedOnce)에서 멱등 보정으로 바꾼 이유(PORTAL-13909):
 * 고정 항목은 이제 작성자가 지울 수 없다. 그래서 '지운 항목을 다시 push 하지 않기' 가드가
 * 필요 없어졌고, 대신 최근 결재라인 적용처럼 목록을 통째로 덮어쓰는 경로 뒤에도 고정 항목이
 * 남아야 한다. 목록이 바뀔 때마다 ensure 를 다시 호출해 스스로 복구되게 한다.
 *
 * 호출부 책임:
 *   - ensure 는 멱등해야 한다(이미 있으면 no-op). 그래야 재호출이 무한 루프로 번지지 않는다.
 *   - ensure 의 useCallback deps 에 대상 목록을 넣어야 덮어쓰기 뒤 보정이 다시 돈다.
 */
const useFixedApprovalMembers = (
  values: ApprovalTypeReferrer[],
  ensureEnabled: boolean,
  ensure: (members: ApprovalTypeReferrer[]) => void,
): UseFixedApprovalMembersResult => {
  const members = useMemo(() => getValidFixedMembers(values), [values]);
  const ids = useMemo(() => toFixedMemberIds(values), [values]);

  useEffect(() => {
    if (!ensureEnabled) return;
    if (members.length === 0) return;
    ensure(members);
  }, [ensureEnabled, members, ensure]);

  const isFixed = useCallback(
    (id?: string | null) => Boolean(id) && ids.has(id as string),
    [ids],
  );

  return { members, isFixed };
};

export default useFixedApprovalMembers;
